import { apiClient, getBlob, getData, getPage, type Page } from '@/api/client'
import type { components } from '@/api/schema'
import { pageTotal } from '@/features/market/api/marketMappers'
import { httpUrlOrNull, resolveLectureSource } from '@/features/courses/lib/lecture-source'
import { knownWord } from '@/schemas/common'
import {
  COURSE_PAYMENT_STATUSES,
  certificatesSchema,
  courseCardSchema,
  courseCheckoutStartSchema,
  courseDetailSchema,
  courseEnrolmentSchema,
  courseHomeSchema,
  coursePaymentListSchema,
  coursePurchaseResultSchema,
  courseReviewsPageSchema,
  dictionaryPageSchema,
  dictionaryTermSchema,
  faqPageSchema,
  learningPathSchema,
  learningPrefsSchema,
  lectureCompletionSchema,
  myLearningSchema,
  pathDeepLinkSchema,
  pathStepDetailSchema,
  pathStepStatusSchema,
  purchasedCourseSchema,
  stepCompletionSchema,
  verifiedCertificateSchema,
  watchProgressSchema,
  type CourseCard,
  type CourseCheckoutStart,
  type CourseDetail,
  type CourseEnrolment,
  type CourseHome,
  type CoursePaymentRecord,
  type CoursePurchaseResult,
  type CourseReviewsPage,
  type Certificates,
  type DictionaryPage,
  type DictionaryQuery,
  type DictionaryTerm,
  type FaqPage,
  type LearningPath,
  type LearningPrefs,
  type LectureCompletion,
  type MyLearning,
  type PageQuery,
  type PathDeepLink,
  type PathStepDetail,
  type PathStepStatus,
  type PurchasedCourse,
  type StepCompletion,
  type VerifiedCertificate,
  type WatchProgress,
} from '@/schemas/courses'

/**
 * The courses seam: mm-be's `/api/v5/courses/` in, app types out.
 *
 * Structured like `features/mutual-funds/api/fundService.ts`: wire types come
 * from `@/api/schema`, never restated, so a regenerated `schema.d.ts` that
 * renames a field breaks the build HERE; every mapper's result goes through
 * its Zod schema before it leaves the module.
 *
 * Not-found answers are NOT handled here — every read throws its ApiError, and
 * `queries.ts` decides which codes mean "nothing there" (`null`) rather than
 * failure. The service stays a plain wire-to-domain translation.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** A string the backend may send as `""` as well as null — both mean "none". */
const blankToNull = (value: string | null): string | null =>
  value === null || value.trim() === '' ? null : value

/**
 * CMS markup that renders as nothing — `""`, `<p>&nbsp;</p>`, `<h2> </h2>` —
 * is `null`, so a screen can hide the tab or section instead of drawing an
 * empty one. Only an emptiness test: the markup itself is sanitised where it
 * is drawn (`components/SafeHtml`), never here.
 */
const htmlOrNull = (value: string | null): string | null => {
  if (value === null) return null
  const text = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, '')
    .replace(/[\s\u00a0\u200b]/g, '')
  return text === '' ? null : value
}

/**
 * mm-be's ceiling on a purchase's access period: 100 years, past which
 * `now + timedelta(days=…)` overflows (`purchases._MAX_ACCESS_DAYS`).
 */
const MAX_ACCESS_DAYS = 36_500

/**
 * `purchase_expires_on` as the two things the app needs from it. mm-be reads a
 * null period as "never lapses" and REFUSES a course whose period is 0 or less,
 * or above its ceiling (400 `COURSE_NOT_PURCHASABLE`, at checkout and at free
 * enrolment), so "unset" and "unusable" must not collapse into one null: the
 * first is a lifetime, the second is a course that cannot be sold.
 */
const accessPeriod = (
  value: number | null,
): { accessDays: number | null; accessPeriodOk: boolean } => {
  if (typeof value !== 'number') return { accessDays: null, accessPeriodOk: true }
  const usable = Number.isInteger(value) && value >= 1 && value <= MAX_ACCESS_DAYS
  return usable
    ? { accessDays: value, accessPeriodOk: true }
    : { accessDays: null, accessPeriodOk: false }
}

const knownStepStatus = (value: string): PathStepStatus | null => {
  const parsed = pathStepStatusSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

const knownDeepLink = (value: string | null): PathDeepLink | null => {
  const parsed = pathDeepLinkSchema.safeParse(value?.trim().toLowerCase())
  return parsed.success ? parsed.data : null
}

const nonBlank = (values: readonly string[]): string[] =>
  values.map((value) => value.trim()).filter((value) => value !== '')

// ── Mappers ──────────────────────────────────────────────────────────────────
//
// Exported so each one is testable on a fixture without a request. They map
// only; the schema at the call site checks what they produce.

/** `slug` and `total_hours` are dropped on purpose — see `courseCardSchema`. */
export const mapCard = (wire: Wire<'CourseCard'>) => ({
  id: wire.id,
  isFree: wire.is_free,
  title: wire.title,
  level: blankToNull(wire.level),
  language: blankToNull(wire.language),
  hasCertificate: wire.certificate,
  thumbnail: httpUrlOrNull(wire.thumbnail),
  price: wire.price,
  discountPrice: wire.discount_price,
  ...accessPeriod(wire.purchase_expires_on),
  lectureCount: wire.total_lectures,
})

export const mapLecture = (wire: Wire<'Lecture'>) => ({
  id: wire.id,
  title: wire.title,
  description: htmlOrNull(wire.description),
  thumbnail: httpUrlOrNull(wire.thumbnail),
  durationMinutes: wire.video_length,
  section: blankToNull(wire.sub_course_title),
  discussionUrl: httpUrlOrNull(wire.discussion_url),
  learningMaterials: htmlOrNull(wire.learning_materials),
  source: resolveLectureSource({
    embedUrl: wire.embed_url,
    video: wire.video,
    videoUrl: wire.video_url,
  }),
  isFree: wire.is_free,
  completed: wire.completed,
  started: wire.watched,
})

/** `curriculum` is dropped on purpose — see `courseDetailSchema`. */
export const mapDetail = (wire: Wire<'CourseDetail'>) => ({
  ...mapCard(wire),
  description: htmlOrNull(wire.description),
  studyMaterials: htmlOrNull(wire.study_materials),
  instructor:
    wire.instructor === null
      ? null
      : {
          id: wire.instructor.id,
          name: wire.instructor.name,
          designation: blankToNull(wire.instructor.designation),
          description: htmlOrNull(wire.instructor.description),
        },
  purchased: wire.purchased,
  ratingAverage: wire.rating_average,
  ratingCount: wire.rating_count,
  lectures: wire.lectures.map(mapLecture),
})

export const mapReview = (wire: Wire<'CourseReview'>) => ({
  id: wire.id,
  rating: wire.rating,
  text: wire.review,
  reviewerName: blankToNull(wire.reviewer_name),
  createdAt: wire.created_on,
})

export const mapMyLearningCourse = (wire: Wire<'MyLearningCourse'>) => ({
  course: mapCard(wire.course),
  lecturesWatched: wire.lectures_watched,
  lecturesTotal: wire.lectures_total,
  finished: wire.finished,
  daysLeft: wire.validity_days_left,
})

export const mapFreeLecture = (wire: Wire<'FreeLecture'>) => ({
  id: wire.id,
  title: wire.title,
  thumbnail: httpUrlOrNull(wire.thumbnail),
  durationMinutes: wire.video_length,
  courseId: wire.course_id,
  courseTitle: wire.course_title,
  source: resolveLectureSource({
    embedUrl: wire.embed_url,
    video: wire.video,
    videoUrl: wire.video_url,
  }),
})

export const mapHome = (wire: Wire<'CourseSections'>) => ({
  resume: wire.resume.map(mapMyLearningCourse),
  freeToWatch: wire.free_to_watch.map(mapFreeLecture),
  startHere: wire.start_here.map(mapCard),
  goingDeeper: wire.going_deeper.map(mapCard),
})

export const mapMyLearning = (wire: Wire<'MyLearning'>) => ({
  summary: {
    coursesTotal: wire.summary.courses_total,
    inProgress: wire.summary.in_progress,
    finished: wire.summary.finished,
    lecturesWatched: wire.summary.lectures_watched,
    lecturesTotal: wire.summary.lectures_total,
  },
  courses: wire.courses.map(mapMyLearningCourse),
})

export const mapPurchased = (wire: Wire<'PurchasedCourse'>) => ({
  id: wire.id,
  course: mapCard(wire.course),
  purchasedAt: wire.purchased_on,
  expiresAt: wire.expires_on,
  certificateIssuedOn: wire.certificate_issued_date,
})

export const mapCertificates = (wire: Wire<'Certificates'>) => ({
  issued: wire.issued.map((row) => ({
    course: mapCard(row.course),
    issuedOn: row.issued_date,
    code: blankToNull(row.code),
  })),
  closeToOne: wire.close_to_one.map(mapMyLearningCourse),
})

export const mapVerifiedCertificate = (wire: Wire<'VerifiedCertificate'>) => ({
  code: wire.code,
  courseTitle: wire.course_title,
  issuedOn: wire.issued_date,
  holderName: blankToNull(wire.holder_name),
})

export const mapPath = (wire: Wire<'Path'>) => ({
  slug: wire.slug,
  title: wire.title,
  tagline: blankToNull(wire.tagline),
  total: wire.total,
  completed: wire.completed,
  showOnHome: wire.show_on_home,
  steps: wire.steps.map((step) => ({
    id: step.id,
    order: step.order,
    title: step.title,
    subtitle: blankToNull(step.subtitle),
    durationSeconds: step.video_length,
    status: knownStepStatus(step.status),
    deepLink: knownDeepLink(step.deep_link),
  })),
})

export const mapPathStep = (wire: Wire<'PathStepDetail'>) => ({
  id: wire.id,
  order: wire.order,
  title: wire.title,
  subtitle: blankToNull(wire.subtitle),
  // A step has only the stream field — no embed, no asset file.
  source: resolveLectureSource({ embedUrl: null, video: null, videoUrl: wire.video_url }),
  durationSeconds: wire.video_length,
  learnPoints: nonBlank(wire.learn_points),
  glossaryTerms: nonBlank(wire.glossary_terms),
  completed: wire.completed,
  total: wire.total,
  nextStepId: wire.next_id,
  nextStepTitle: blankToNull(wire.next_title),
})

export const mapDictionaryTerm = (wire: Wire<'DictionaryTermDetail'>) => ({
  slug: wire.slug,
  term: wire.term,
  termNepali: blankToNull(wire.term_nepali),
  shortDef: wire.short_def,
  oneLine: wire.one_line,
  facts: wire.facts,
  example:
    wire.example === null
      ? null
      : {
          symbol: blankToNull(wire.example.symbol),
          name: wire.example.name,
          price: wire.example.price,
        },
  related: wire.related,
})

/**
 * `fields` defaults to `{}`: always on the wire, but optional in the OpenAPI.
 * No `discount` — mm-be takes no code for a course and sends none back.
 *
 * The hand-off is mapped the way the subscription's `mapCheckoutStart` maps
 * it and parsed by the same `gatewayHandoffSchema`, so a method this build
 * does not know fails here for a course exactly as it does for a plan.
 */
export const mapCourseCheckoutStart = (wire: Wire<'CourseCheckoutStartResponse'>) => ({
  transactionId: wire.transaction_id,
  amount: wire.amount,
  gateway: wire.gateway,
  handoff:
    wire.params.method === 'form_post'
      ? { method: wire.params.method, url: wire.params.url, fields: wire.params.fields ?? {} }
      : { method: wire.params.method, url: wire.params.url },
})

export const mapCoursePurchaseResult = (wire: Wire<'CoursePurchaseResult'>) => ({
  transactionId: wire.transaction_id,
  courseId: wire.course_id,
  courseTitle: wire.course_title,
  expiresOn: wire.expires_on,
  accessActive: wire.access_active,
  alreadyCompleted: wire.already_completed,
})

export const mapCourseEnrolment = (wire: Wire<'CourseEnrolment'>) => ({
  courseId: wire.course_id,
  courseTitle: wire.course_title,
  expiresOn: wire.expires_on,
  alreadyEnrolled: wire.already_enrolled,
})

export const mapCoursePayment = (wire: Wire<'CoursePaymentRecord'>) => ({
  transactionId: wire.transaction_id,
  courseId: wire.course_id,
  courseTitle: wire.course_title,
  amount: wire.amount,
  method: wire.method,
  status: knownWord(COURSE_PAYMENT_STATUSES, wire.status),
  startedOn: wire.started_on,
  completedOn: wire.completed_on,
})

/**
 * One server page, with the grand total from `meta.total` — never
 * `meta.count`, which mm-be sets to THIS page's row count
 * (`newsletterService`'s rule 1 explains the trap).
 */
const toPage = <Row>(result: Page<unknown>, rows: Row[], query: PageQuery) => {
  const total = pageTotal(result.meta, rows.length)
  const totalPages = result.meta?.total_pages
  return {
    rows,
    page: result.meta?.page ?? query.page,
    pageSize: result.meta?.page_size ?? query.pageSize,
    total,
    totalPages:
      typeof totalPages === 'number' ? totalPages : Math.ceil(total / Math.max(1, query.pageSize)),
  }
}

/**
 * The catalog in one request, at mm-be's page-size ceiling. Seven courses on
 * dev; the home screen filters it client-side by level (the endpoint takes no
 * filter).
 */
const CATALOG_PAGE_SIZE = 200

// ── The endpoints ────────────────────────────────────────────────────────────

export const courseService = {
  /** Every course the catalog lists — active, not recorded, not coming soon. */
  list: async (): Promise<CourseCard[]> => {
    const { rows } = await getPage<Wire<'CourseCard'>>(() =>
      apiClient.GET('/api/v5/courses/', {
        params: { query: { page: 1, page_size: CATALOG_PAGE_SIZE } },
      }),
    )
    return courseCardSchema.array().parse(rows.map(mapCard))
  },

  /** The home shelves. Member-aware: `resume` is empty signed out. */
  home: async (): Promise<CourseHome> => {
    const data = await getData<Wire<'CourseSections'>>(() => apiClient.GET('/api/v5/courses/home/'))
    return courseHomeSchema.parse(mapHome(data))
  },

  /** One course with its lectures. 404 `COURSE_NOT_FOUND` for an unknown or inactive id. */
  detail: async (courseId: number): Promise<CourseDetail> => {
    const data = await getData<Wire<'CourseDetail'>>(() =>
      apiClient.GET('/api/v5/courses/{course_id}/', {
        params: { path: { course_id: courseId } },
      }),
    )
    return courseDetailSchema.parse(mapDetail(data))
  },

  /** One page of a course's reviews, newest first. */
  reviews: async (courseId: number, query: PageQuery): Promise<CourseReviewsPage> => {
    const result = await getPage<Wire<'CourseReview'>>(() =>
      apiClient.GET('/api/v5/courses/{course_id}/reviews/', {
        params: {
          path: { course_id: courseId },
          query: { page: query.page, page_size: query.pageSize },
        },
      }),
    )
    return courseReviewsPageSchema.parse(toPage(result, result.rows.map(mapReview), query))
  },

  /** One page of the global FAQ, oldest first. */
  questions: async (query: PageQuery): Promise<FaqPage> => {
    const result = await getPage<Wire<'Faq'>>(() =>
      apiClient.GET('/api/v5/courses/questions/', {
        params: { query: { page: query.page, page_size: query.pageSize } },
      }),
    )
    return faqPageSchema.parse(toPage(result, result.rows, query))
  },

  /** The member's progress rollup and every active purchase with its progress. */
  myLearning: async (): Promise<MyLearning> => {
    const data = await getData<Wire<'MyLearning'>>(() =>
      apiClient.GET('/api/v5/courses/my-learning/'),
    )
    return myLearningSchema.parse(mapMyLearning(data))
  },

  /** The member's active (unexpired) purchases, newest first. */
  purchased: async (): Promise<PurchasedCourse[]> => {
    const { rows } = await getPage<Wire<'PurchasedCourse'>>(() =>
      apiClient.GET('/api/v5/courses/purchased/'),
    )
    return purchasedCourseSchema.array().parse(rows.map(mapPurchased))
  },

  /** Issued certificates, newest first, and the courses close to one. */
  certificates: async (): Promise<Certificates> => {
    const data = await getData<Wire<'Certificates'>>(() =>
      apiClient.GET('/api/v5/courses/certificates/'),
    )
    return certificatesSchema.parse(mapCertificates(data))
  },

  /**
   * The certificate itself, a PDF. Owner-only: the bearer token is all it
   * needs, and a code that is not the member's is `CERTIFICATE_NOT_FOUND`.
   * Anything but `application/pdf` rejects with a `FileTypeError`.
   */
  certificatePdf: async (code: string): Promise<Blob> =>
    getBlob(
      () =>
        apiClient.GET('/api/v5/courses/certificates/{code}/download/', {
          params: { path: { code } },
          parseAs: 'blob',
        }),
      'application/pdf',
    ),

  /** PUBLIC. 404 `CERTIFICATE_NOT_FOUND` for a code mm-be never issued. */
  verifyCertificate: async (code: string): Promise<VerifiedCertificate> => {
    const data = await getData<Wire<'VerifiedCertificate'>>(() =>
      apiClient.GET('/api/v5/courses/certificates/verify/{code}/', {
        params: { path: { code } },
      }),
    )
    return verifiedCertificateSchema.parse(mapVerifiedCertificate(data))
  },

  /** The active learning path. 404 `LEARNING_PATH_NOT_FOUND` until one is authored. */
  path: async (): Promise<LearningPath> => {
    const data = await getData<Wire<'Path'>>(() => apiClient.GET('/api/v5/courses/path/'))
    return learningPathSchema.parse(mapPath(data))
  },

  /** One step. 404 `LEARNING_STEP_NOT_FOUND` for an unknown id. */
  pathStep: async (stepId: number): Promise<PathStepDetail> => {
    const data = await getData<Wire<'PathStepDetail'>>(() =>
      apiClient.GET('/api/v5/courses/path/steps/{step_id}/', {
        params: { path: { step_id: stepId } },
      }),
    )
    return pathStepDetailSchema.parse(mapPathStep(data))
  },

  /** A page of dictionary terms, A–Z, optionally matching `q` (term or slug). */
  dictionary: async (query: DictionaryQuery): Promise<DictionaryPage> => {
    const result = await getPage<Wire<'DictionaryTermCard'>>(() =>
      apiClient.GET('/api/v5/courses/dictionary/', {
        params: {
          query: {
            page: query.page,
            page_size: query.pageSize,
            ...(query.q === null ? {} : { q: query.q }),
          },
        },
      }),
    )
    const rows = result.rows.map((row) => ({
      slug: row.slug,
      term: row.term,
      shortDef: row.short_def,
    }))
    return dictionaryPageSchema.parse(toPage(result, rows, query))
  },

  /** One term. 404 `DICTIONARY_TERM_NOT_FOUND` for an unknown or inactive slug. */
  dictionaryTerm: async (slug: string): Promise<DictionaryTerm> => {
    const data = await getData<Wire<'DictionaryTermDetail'>>(() =>
      apiClient.GET('/api/v5/courses/dictionary/{slug}/', {
        params: { path: { slug } },
      }),
    )
    return dictionaryTermSchema.parse(mapDictionaryTerm(data))
  },

  // ── Writes ─────────────────────────────────────────────────────────────────

  /**
   * Saves how far into a lecture the member has watched.
   *
   * `min_watched` is the playback POSITION IN SECONDS as a decimal string
   * ("598") — mm-be's documented format, `float()`-parsed and compared
   * against the lecture's `video_length` to decide "watched to the end"
   * (within 5s). Owned lectures only: a free lecture in an unowned course
   * answers 403 `COURSE_NOT_PURCHASED`.
   *
   * TODO(tech-debt): mm-be compares this SECONDS position against
   * `video_length`, which is MINUTES in every real course — so a 13-minute
   * lecture counts as watched once 8 seconds are saved, and a course's
   * certificate can issue after a few seconds of each lecture. Sent as
   * seconds regardless (the documented contract); mm-be has to convert.
   */
  saveWatch: async (lectureId: number, positionSeconds: number): Promise<WatchProgress> => {
    const data = await getData<Wire<'WatchProgress'>>(() =>
      apiClient.POST('/api/v5/courses/lectures/{lecture_id}/watch/', {
        params: { path: { lecture_id: lectureId } },
        body: { min_watched: String(Math.max(0, Math.floor(positionSeconds))) },
      }),
    )
    return watchProgressSchema.parse({ lectureId: data.lecture_id, watched: data.watched })
  },

  /**
   * FLIPS the member's "complete" mark on a lecture — mm-be's endpoint is a
   * toggle, not a set: posting to a completed lecture un-completes it. The
   * answer is the state it settled on. Owned lectures only (403 otherwise).
   */
  toggleLectureComplete: async (lectureId: number): Promise<LectureCompletion> => {
    const data = await getData<Wire<'LectureProgress'>>(() =>
      apiClient.POST('/api/v5/courses/lectures/{lecture_id}/complete/', {
        params: { path: { lecture_id: lectureId } },
      }),
    )
    return lectureCompletionSchema.parse({ lectureId: data.lecture_id, completed: data.completed })
  },

  /** Marks a path step done (idempotent — never un-marks). Unlocks the next. */
  completePathStep: async (stepId: number): Promise<StepCompletion> => {
    const data = await getData<Wire<'StepCompletion'>>(() =>
      apiClient.POST('/api/v5/courses/path/steps/{step_id}/complete/', {
        params: { path: { step_id: stepId } },
      }),
    )
    return stepCompletionSchema.parse({ stepId: data.step_id, completed: data.completed })
  },

  /** The "show my path on Home" preference. */
  setPathPrefs: async (prefs: LearningPrefs): Promise<LearningPrefs> => {
    const data = await getData<Wire<'LearningPrefs'>>(() =>
      apiClient.PATCH('/api/v5/courses/path/prefs/', {
        body: { show_on_home: prefs.showOnHome },
      }),
    )
    return learningPrefsSchema.parse({ showOnHome: data.show_on_home })
  },

  // ── Buying ─────────────────────────────────────────────────────────────────

  /**
   * WRITES a Pending payment row before it answers, so every call is a new
   * attempt — call it once per explicit Pay. There is no amount and no code to
   * send: mm-be prices the course itself (its `discountPrice`).
   *
   * By `error.code`: 409 `COURSE_ALREADY_OWNED`; 400 `COURSE_IS_FREE` (enrol
   * instead) and `COURSE_NOT_PURCHASABLE` (no price, or no usable access
   * period — a data fault, so "not on sale", never "free"); 404
   * `COURSE_NOT_FOUND`; 503 `COURSE_PAYMENTS_DISABLED` (every course, every
   * gateway) and `PAYMENT_GATEWAY_UNAVAILABLE` (this gateway only); 429
   * `RATE_LIMITED`.
   */
  startCourseCheckout: async (
    courseId: number,
    gateway: Wire<'GatewayEnum'>,
  ): Promise<CourseCheckoutStart> => {
    const data = await getData<Wire<'CourseCheckoutStartResponse'>>(() =>
      apiClient.POST('/api/v5/courses/{course_id}/checkout/', {
        params: { path: { course_id: courseId } },
        body: { gateway },
      }),
    )
    return courseCheckoutStartSchema.parse(mapCourseCheckoutStart(data))
  },

  /**
   * Idempotent: an attempt already granted answers 200 `alreadyCompleted`.
   * Not gated by the course kill switch — a payment already taken is always
   * settled — and scoped to the caller's own attempts, so an unknown id and
   * someone else's are both 404 `PAYMENT_NOT_FOUND`. A SUBSCRIPTION attempt's
   * id is also `PAYMENT_NOT_FOUND` here: each product confirms only its own.
   * `esewaData` is eSewa's raw `data` blob, the first confirm of an eSewa
   * return only. The other refusals are the subscription confirm's: 402
   * `PAYMENT_NOT_VERIFIED`, 409 `PAYMENT_AMOUNT_MISMATCH`, 503
   * `PAYMENT_GATEWAY_UNAVAILABLE`.
   */
  confirmCoursePayment: async (
    transactionId: string,
    esewaData?: string | null,
  ): Promise<CoursePurchaseResult> => {
    const data = await getData<Wire<'CoursePurchaseResult'>>(() =>
      apiClient.POST('/api/v5/courses/checkout/confirm/', {
        body: {
          transaction_id: transactionId,
          ...(esewaData === undefined || esewaData === null ? {} : { esewa_data: esewaData }),
        },
      }),
    )
    return coursePurchaseResultSchema.parse(mapCoursePurchaseResult(data))
  },

  /**
   * Joins a FREE course (`isFree`): no gateway, no payment row. Idempotent —
   * a repeat answers 201 with `alreadyEnrolled` true.
   *
   * By `error.code`: 400 `COURSE_NOT_FREE` (check out instead); 400
   * `COURSE_NOT_PURCHASABLE` (a free course whose access period is unusable —
   * mm-be refuses it here too, though the OpenAPI lists it for checkout
   * only); 404 `COURSE_NOT_FOUND`; 503 `COURSE_PAYMENTS_DISABLED`, which stops
   * free enrolment as well as sales.
   */
  enrolFreeCourse: async (courseId: number): Promise<CourseEnrolment> => {
    const data = await getData<Wire<'CourseEnrolment'>>(() =>
      apiClient.POST('/api/v5/courses/{course_id}/enrol/', {
        params: { path: { course_id: courseId } },
      }),
    )
    return courseEnrolmentSchema.parse(mapCourseEnrolment(data))
  },

  /**
   * The member's own course payment attempts, newest first, at most 100
   * (`MAX_PAYMENT_HISTORY`) — Pending and Cancelled ones included.
   * Subscription payments are never in it; each product lists only its own.
   */
  listCoursePayments: async (): Promise<CoursePaymentRecord[]> => {
    const { rows } = await getPage<Wire<'CoursePaymentRecord'>>(() =>
      apiClient.GET('/api/v5/courses/payments/'),
    )
    return coursePaymentListSchema.parse(rows.map(mapCoursePayment))
  },
}
